import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import Select, { SingleValue } from 'react-select'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'
import { getCrewClaims, getCompanyList, getCompanyAdminList, getCrewList, CrewClaim } from '../core/_requests'
import { allowCompanyPresentationFilters } from '../core/crewingPresentationFilters'
import { Crew, Company, CompanyAdmin, Vessel } from '../core/_models'

type CrewClaimExportModalProps = {
  open: boolean
  onClose: () => void
  initialFilters: {
    companyGroupId?: number | null
    companyAdminId?: number | null
    vesselId?: number | null
    crewId?: number | null
    month?: string | null
    selectedColumns?: string[] | null
  }
  currentUser: any
  isSuperAdmin: boolean
  isCompanyAdmin: boolean
  isCompanyGroupAdmin: boolean
  isOperator: boolean
  isCrewMaster: boolean
  isCrew: boolean
  vessels: Vessel[]
}

type ExportFormState = {
  companyGroupId?: number | null
  companyAdminId?: number | null
  vesselId?: number | null
  crewId?: number | null
  month?: string | null
  selectedColumns: string[]
}

type ExportColumnOption = {
  key: string
  label: string
}

const EXPORT_COLUMN_OPTIONS: ExportColumnOption[] = [
  { key: 'CREW_NAME', label: 'Crew Name' },
  { key: 'RANK', label: 'Rank' },
  { key: 'VESSEL', label: 'Vessel' },
  { key: 'MONTH', label: 'Month' },
  { key: 'STATUS', label: 'Status' },
  { key: 'SALARY', label: 'Salary' },
  { key: 'MEDICAL', label: 'Medical' },
  { key: 'SAFETY_GEAR', label: 'Safety Gear' },
  { key: 'TRAVEL', label: 'Travel' },
  { key: 'BOARDING_LODGING', label: 'Boarding & Lodging' },
  { key: 'HRA_ALLOWANCE', label: 'HRA Allowance' },
  { key: 'BONUS', label: 'Bonus' },
  { key: 'MISC_REIMBURSEMENTS', label: 'Misc Reimbursements' },
  { key: 'OVERTIME_AMOUNT', label: 'Overtime Amount' },
  { key: 'TOTAL_AMOUNT', label: 'Total Amount' },
  { key: 'REMARKS', label: 'Remarks' },
  { key: 'OFFICE_REMARKS', label: 'Office Remarks' },
]

const DEFAULT_EXPORT_COLUMN_KEYS = EXPORT_COLUMN_OPTIONS.map((option) => option.key)

const normalizeSelectedColumns = (selectedColumns?: string[] | null): string[] => {
  const nextColumns = Array.isArray(selectedColumns)
    ? selectedColumns
        .map((value) => String(value || '').trim())
        .filter((value) => DEFAULT_EXPORT_COLUMN_KEYS.includes(value))
    : []
  return nextColumns.length > 0 ? Array.from(new Set(nextColumns)) : [...DEFAULT_EXPORT_COLUMN_KEYS]
}

const resolveCurrentVesselId = (currentUser: any): number | null =>
  Number(currentUser?.vessel?.id ?? currentUser?.vesselId ?? 0) || null

const buildDefaultForm = (
  initialFilters: CrewClaimExportModalProps['initialFilters'],
  currentUser: any,
  isCrew: boolean
): ExportFormState => ({
  companyGroupId: initialFilters.companyGroupId ?? null,
  companyAdminId: initialFilters.companyAdminId ?? null,
  vesselId: isCrew ? resolveCurrentVesselId(currentUser) : initialFilters.vesselId ?? null,
  crewId: initialFilters.crewId ?? null,
  month: initialFilters.month || null,
  selectedColumns: normalizeSelectedColumns(initialFilters.selectedColumns),
})

const toNumber = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const formatCurrency = (value: number) =>
  value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const formatMonthLabel = (monthKey: string): string => {
  if (!monthKey) return '-'
  const [yearStr, monthStr] = monthKey.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return monthKey
  }
  return new Date(year, month - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
}

export const CrewClaimExportModal: React.FC<CrewClaimExportModalProps> = ({
  open,
  onClose,
  initialFilters,
  currentUser,
  isSuperAdmin,
  isCompanyAdmin,
  isCompanyGroupAdmin,
  isOperator,
  isCrewMaster,
  isCrew,
  vessels,
}) => {
  const [form, setForm] = useState<ExportFormState>(() =>
    buildDefaultForm(initialFilters, currentUser, isCrew)
  )
  const [companies, setCompanies] = useState<Company[]>([])
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([])
  const [crewMembers, setCrewMembers] = useState<Crew[]>([])
  const [claims, setClaims] = useState<CrewClaim[]>([])
  const [loadingClaims, setLoadingClaims] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Sub-Company list filtered from companies[] by selected company
  const filteredSubCompanies = useMemo(() => {
    if (!form.companyGroupId) return companies
    return companies.filter((ca) => {
      const parentId = (ca as any)?.cga?.id ?? (ca as any)?.companyGroupAdminId
      return parentId === Number(form.companyGroupId)
    })
  }, [companies, form.companyGroupId])

  // Filter vessels by selected subcompany first, then by company
  const filteredVessels = useMemo(() => {
    const getVesselCompanyAdminId = (v: Vessel): number | undefined =>
      v.companyAdmin?.id ?? (v as any).companyAdminId ?? undefined
    const getVesselCompanyGroupId = (v: Vessel): number | undefined =>
      (v as any).companyGroupAdminId ??
      v.companyGroupAdmin?.id ??
      (v.companyAdmin as any)?.cgaid?.id ??
      v.companyAdmin?.cga?.id

    if (form.companyAdminId) {
      return vessels.filter((v) => getVesselCompanyAdminId(v) === Number(form.companyAdminId))
    }
    if (form.companyGroupId) {
      return vessels.filter((v) => getVesselCompanyGroupId(v) === Number(form.companyGroupId))
    }
    return vessels
  }, [vessels, form.companyGroupId, form.companyAdminId])

  // Filter crew by vessel client-side
  const filteredCrewList = useMemo(() => {
    if (!form.vesselId) return crewMembers
    const targetVesselId = Number(form.vesselId)
    return crewMembers.filter((c) => {
      const crewVesselId = Number(c.vesselId || c.vessel?.id)
      return crewVesselId === targetVesselId
    })
  }, [crewMembers, form.vesselId])

  const showCompanyFilter = isSuperAdmin
  const showSubCompanyFilter = isSuperAdmin || isCompanyAdmin
  const allowCompanyFilters = allowCompanyPresentationFilters(isSuperAdmin, currentUser?.role?.id, isOperator)

  const shouldShowSubCompanyFilter = isCompanyGroupAdmin
    ? filteredSubCompanies.length > 0
    : showCompanyFilter && companies.length > 0 && !!form.companyGroupId

  const selectedColumnCount = form.selectedColumns?.length ?? 0

  // Reset vessel if it no longer belongs to the selected company/subcompany
  useEffect(() => {
    if (!form.vesselId) return
    const exists = filteredVessels.some((v) => v.id === form.vesselId)
    if (!exists) setForm((prev) => ({ ...prev, vesselId: null, crewId: null }))
  }, [filteredVessels, form.vesselId])

  // Reset crew if it no longer belongs to the selected filters
  useEffect(() => {
    if (!form.crewId) return
    const exists = filteredCrewList.some((c) => c.id === form.crewId)
    if (!exists) setForm((prev) => ({ ...prev, crewId: null }))
  }, [filteredCrewList, form.crewId])

  // Reset form when modal opens
  useEffect(() => {
    if (!open) return
    setForm(buildDefaultForm(initialFilters, currentUser, isCrew))
    setError(null)
    setClaims([])
  }, [open, initialFilters, currentUser, isCrew])

  // Fetch company/sub-company lists
  useEffect(() => {
    if (!open) return
    if (showSubCompanyFilter || isCompanyGroupAdmin) {
      getCompanyList()
        .then((data) => setCompanies(data || []))
        .catch(() => setCompanies([]))
    }
    if (showCompanyFilter) {
      getCompanyAdminList()
        .then((data) => setCompanyAdmins(data || []))
        .catch(() => setCompanyAdmins([]))
    }
  }, [open, showCompanyFilter, showSubCompanyFilter, isCompanyGroupAdmin])

  // Fetch all claims when modal opens
  useEffect(() => {
    if (!open) return
    let cancelled = false
    const loadClaims = async () => {
      setLoadingClaims(true)
      try {
        const data = await getCrewClaims()
        if (!cancelled) setClaims(data || [])
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load claims')
      } finally {
        if (!cancelled) setLoadingClaims(false)
      }
    }
    void loadClaims()
    return () => { cancelled = true }
  }, [open])

  // Fetch crew members when modal opens or filters change
  useEffect(() => {
    if (!open) return
    const companyGroupId = isCompanyGroupAdmin
      ? (form.companyGroupId ? Number(form.companyGroupId) : (currentUser as any)?.companyGroupAdminId ?? (currentUser as any)?.companyGroupAdmin?.id ?? undefined)
      : (allowCompanyFilters && form.companyGroupId ? Number(form.companyGroupId) : undefined)
    const companyAdminId = allowCompanyFilters && form.companyAdminId ? Number(form.companyAdminId) : undefined
    getCrewList({ companyGroupId, companyAdminId })
      .then((data) => setCrewMembers(data || []))
      .catch(() => setCrewMembers([]))
  }, [open, form.companyGroupId, form.companyAdminId, allowCompanyFilters, isCompanyGroupAdmin, currentUser])

  const handleFieldChange = (name: keyof ExportFormState, value: string) => {
    const nextValue = value === '' ? null : value
    setForm((prev) => ({
      ...prev,
      [name]: ['companyGroupId', 'companyAdminId', 'vesselId', 'crewId'].includes(name)
        ? nextValue === null ? null : Number(nextValue)
        : nextValue,
      ...(name === 'companyGroupId' ? { companyAdminId: null, vesselId: null, crewId: null } : {}),
      ...(name === 'companyAdminId' ? { vesselId: null, crewId: null } : {}),
      ...(name === 'vesselId' ? { crewId: null } : {}),
    }))
  }

  const handleToggleSelectedColumn = (columnKey: string) => {
    setForm((prev) => {
      const currentColumns = prev.selectedColumns || []
      const nextColumns = currentColumns.includes(columnKey)
        ? currentColumns.filter((value) => value !== columnKey)
        : [...currentColumns, columnKey]
      return { ...prev, selectedColumns: nextColumns }
    })
  }

  const handleSelectAllFields = () => {
    setForm((prev) => ({ ...prev, selectedColumns: [...DEFAULT_EXPORT_COLUMN_KEYS] }))
  }

  const handleClearFields = () => {
    setForm((prev) => ({ ...prev, selectedColumns: [] }))
  }

  const handleClear = () => {
    setForm((prev) => ({
      ...buildDefaultForm(initialFilters, currentUser, isCrew),
      selectedColumns:
        prev.selectedColumns && prev.selectedColumns.length > 0
          ? prev.selectedColumns
          : [...DEFAULT_EXPORT_COLUMN_KEYS],
    }))
    setError(null)
  }

  const filteredClaims = useMemo(() => {
    let filtered = claims

    // Scope to crew the server returned for this role
    if (crewMembers.length > 0) {
      const scopedCrewIds = new Set(crewMembers.map((c) => c.id))
      filtered = filtered.filter((d) => scopedCrewIds.has(d.crewId))
    }
    if (form.vesselId) {
      filtered = filtered.filter((d) => d.vesselId === Number(form.vesselId))
    }
    if (form.crewId) {
      filtered = filtered.filter((d) => d.crewId === Number(form.crewId))
    }
    if (form.month) {
      filtered = filtered.filter((d) => d.claimMonth?.startsWith(form.month || ''))
    }

    return filtered
  }, [claims, form, crewMembers, allowCompanyFilters])

  const handleExport = async () => {
    if (!form.selectedColumns || form.selectedColumns.length === 0) {
      setError('Select at least one field to export')
      return
    }
    if (filteredClaims.length === 0) {
      setError('No claims match the current filters')
      return
    }

    setExporting(true)
    setError(null)
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const marginX = 30

      doc.setFontSize(18)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(28, 72, 126)
      doc.text('Crew Claims Report', marginX, 36)

      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(100, 100, 100)

      const filterLabels: string[] = []
      if (form.month) filterLabels.push(`Month: ${formatMonthLabel(form.month)}`)
      if (form.companyGroupId) {
        const company = companyAdmins.find((c) => c.id === Number(form.companyGroupId))
        if (company) filterLabels.push(`Company: ${company.name}`)
      }
      if (form.companyAdminId) {
        const admin = filteredSubCompanies.find((c) => c.id === Number(form.companyAdminId))
        if (admin) filterLabels.push(`Sub-Company: ${admin.name}`)
      }
      if (form.vesselId) {
        const vessel = filteredVessels.find((v) => v.id === Number(form.vesselId))
        if (vessel) filterLabels.push(`Vessel: ${vessel.fleet_name}`)
      }
      if (filterLabels.length > 0) {
        doc.text(filterLabels.join('  |  '), marginX, 52)
      } else {
        doc.text('All Records', marginX, 52)
      }

      const rightText = `Total Records: ${filteredClaims.length}  |  Generated: ${new Date().toLocaleDateString()}`
      doc.text(rightText, pageWidth - marginX - doc.getTextWidth(rightText), 52)

      const numericColumns = new Set([
        'SALARY',
        'MEDICAL',
        'SAFETY_GEAR',
        'TRAVEL',
        'BOARDING_LODGING',
        'HRA_ALLOWANCE',
        'BONUS',
        'MISC_REIMBURSEMENTS',
        'OVERTIME_AMOUNT',
        'TOTAL_AMOUNT',
      ])

      const headers = form.selectedColumns.map((key) => {
        const option = EXPORT_COLUMN_OPTIONS.find((opt) => opt.key === key)
        return option?.label || key
      })

      const body = filteredClaims.map((claim) =>
        form.selectedColumns.map((key) => {
          switch (key) {
            case 'CREW_NAME':              return claim.crewName || '-'
            case 'RANK':                   return claim.rankName || '-'
            case 'VESSEL':                 return claim.vesselName || '-'
            case 'MONTH':                  return claim.claimMonth ? formatMonthLabel(claim.claimMonth.slice(0, 7)) : '-'
            case 'STATUS':                 return claim.status || '-'
            case 'SALARY':                 return formatCurrency(toNumber(claim.salary))
            case 'MEDICAL':                return formatCurrency(toNumber(claim.medical))
            case 'SAFETY_GEAR':            return formatCurrency(toNumber(claim.safetyGear))
            case 'TRAVEL':                 return formatCurrency(toNumber(claim.travel))
            case 'BOARDING_LODGING':       return formatCurrency(toNumber(claim.boardingLodging))
            case 'HRA_ALLOWANCE':          return formatCurrency(toNumber(claim.hraAllowance))
            case 'BONUS':                  return formatCurrency(toNumber(claim.bonus))
            case 'MISC_REIMBURSEMENTS':    return formatCurrency(toNumber(claim.miscReimbursements))
            case 'OVERTIME_AMOUNT':        return formatCurrency(toNumber(claim.overtimeAmount))
            case 'TOTAL_AMOUNT':           return formatCurrency(toNumber(claim.totalAmount))
            case 'REMARKS':                return claim.remarks || '-'
            case 'OFFICE_REMARKS':         return claim.officeRemarks || '-'
            default:                       return '-'
          }
        })
      )

      const columnStyles: Record<number, any> = {}
      form.selectedColumns.forEach((colKey, index) => {
        if (numericColumns.has(colKey)) {
          columnStyles[index] = { halign: 'right' }
        }
      })

      ;(doc as any).autoTable({
        startY: 64,
        head: [headers],
        body,
        styles: {
          fontSize: 7,
          cellPadding: { top: 4, right: 3, bottom: 4, left: 3 },
          overflow: 'linebreak',
          valign: 'middle',
        },
        headStyles: {
          fillColor: [28, 72, 126],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle',
        },
        bodyStyles: { textColor: [30, 30, 30] },
        alternateRowStyles: { fillColor: [248, 249, 250] },
        margin: { left: marginX, right: marginX, bottom: 30 },
        theme: 'grid',
        columnStyles,
        didDrawPage: (data: any) => {
          const str = `Page ${data.pageNumber}`
          doc.setFontSize(8)
          doc.setTextColor(130, 130, 130)
          doc.text(str, pageWidth - marginX - doc.getTextWidth(str), pageHeight - 15)
          doc.text('Dexpert - Crew Claims Management System', marginX, pageHeight - 15)
        },
      })

      doc.save(`crew_claims_export_${form.month || 'all'}_${new Date().toISOString().split('T')[0]}.pdf`)
      toast.success('Crew claims PDF exported successfully')
      onClose()
    } catch (e: any) {
      setError(e?.message || 'Export failed')
      showErrorToast(e, 'Failed to export crew claims PDF', 'load')
    } finally {
      setExporting(false)
    }
  }

  if (!open) return null

  return (
    <div className='modal d-block' style={{ backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 2050 }}>
      <div className='modal-dialog modal-dialog-centered modal-xl'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title text-dark'>Export Crew Claims (PDF)</h5>
            <button type='button' className='btn-close' aria-label='Close' onClick={onClose} disabled={exporting} />
          </div>
          <div className='modal-body'>
            <div className='row g-3'>
              {showSubCompanyFilter && (
                <div className='col-md-3'>
                  <label className='form-label fw-semibold text-dark'>Company</label>
                  <Select
                    value={
                      form.companyGroupId && companyAdmins.find((c) => c.id === form.companyGroupId)
                        ? { value: String(form.companyGroupId), label: companyAdmins.find((c) => c.id === form.companyGroupId)!.name }
                        : null
                    }
                    onChange={(opt: SingleValue<{ value: string; label: string }>) =>
                      handleFieldChange('companyGroupId', opt?.value || '')
                    }
                    options={[
                      { value: '', label: 'All Companies' },
                      ...companyAdmins.map((c) => ({ value: String(c.id), label: c.name })),
                    ]}
                    isDisabled={exporting}
                    className='react-select-container'
                    classNamePrefix='react-select'
                    isSearchable
                    placeholder='All Companies'
                    isClearable
                  />
                </div>
              )}
              {shouldShowSubCompanyFilter && (
                <div className='col-md-3'>
                  <label className='form-label fw-semibold text-dark'>Sub-Company</label>
                  <Select
                    value={
                      form.companyAdminId && filteredSubCompanies.find((c) => c.id === form.companyAdminId)
                        ? { value: String(form.companyAdminId), label: filteredSubCompanies.find((c) => c.id === form.companyAdminId)!.name }
                        : null
                    }
                    onChange={(opt: SingleValue<{ value: string; label: string }>) =>
                      handleFieldChange('companyAdminId', opt?.value || '')
                    }
                    options={[
                      { value: '', label: 'All Sub-Companies' },
                      ...filteredSubCompanies.map((c) => ({ value: String(c.id), label: c.name })),
                    ]}
                    isDisabled={exporting || filteredSubCompanies.length === 0}
                    className='react-select-container'
                    classNamePrefix='react-select'
                    isSearchable
                    placeholder='All Sub-Companies'
                    isClearable
                  />
                </div>
              )}
              {(isSuperAdmin || isCompanyAdmin || isCompanyGroupAdmin || isOperator) && (
                <div className='col-md-3'>
                  <label className='form-label fw-semibold text-dark'>Vessel</label>
                  <Select
                    value={
                      form.vesselId && filteredVessels.find((v) => v.id === form.vesselId)
                        ? { value: String(form.vesselId), label: filteredVessels.find((v) => v.id === form.vesselId)!.fleet_name }
                        : null
                    }
                    onChange={(opt: SingleValue<{ value: string; label: string }>) =>
                      handleFieldChange('vesselId', opt?.value || '')
                    }
                    options={[
                      { value: '', label: 'All Vessels' },
                      ...filteredVessels.map((v) => ({ value: String(v.id), label: v.fleet_name })),
                    ]}
                    isDisabled={exporting}
                    className='react-select-container'
                    classNamePrefix='react-select'
                    isSearchable
                    placeholder='All Vessels'
                    isClearable
                  />
                </div>
              )}
              {(isSuperAdmin || isCompanyAdmin || isCompanyGroupAdmin || isOperator || isCrewMaster) && (
                <div className='col-md-3'>
                  <label className='form-label fw-semibold text-dark'>Crew</label>
                  <Select
                    value={
                      form.crewId && filteredCrewList.find((c) => c.id === form.crewId)
                        ? { value: String(form.crewId), label: filteredCrewList.find((c) => c.id === form.crewId)!.name }
                        : null
                    }
                    onChange={(opt: SingleValue<{ value: string; label: string }>) =>
                      handleFieldChange('crewId', opt?.value || '')
                    }
                    options={[
                      { value: '', label: 'All Crew' },
                      ...filteredCrewList.map((c) => ({ value: String(c.id), label: c.name })),
                    ]}
                    isDisabled={exporting}
                    className='react-select-container'
                    classNamePrefix='react-select'
                    isSearchable
                    placeholder='All Crew'
                    isClearable
                  />
                </div>
              )}
              <div className='col-md-3'>
                <label className='form-label fw-semibold text-dark'>Month</label>
                <input
                  type='month'
                  className='form-control'
                  value={form.month ?? ''}
                  onChange={(e) => handleFieldChange('month', e.target.value)}
                  disabled={exporting}
                />
              </div>
            </div>

            <div className='border rounded p-3 mt-4'>
              <div className='d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3'>
                <div>
                  <div className='fw-semibold text-dark'>Select fields to export</div>
                  <div className='text-muted small'>{selectedColumnCount} field(s) selected</div>
                </div>
                <div className='d-flex flex-wrap gap-2'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={handleSelectAllFields}
                    disabled={exporting}
                  >
                    Select All Fields
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    onClick={handleClearFields}
                    disabled={exporting}
                  >
                    Clear Fields
                  </button>
                </div>
              </div>
              <div className='row g-2'>
                {EXPORT_COLUMN_OPTIONS.map((option) => {
                  const checked = form.selectedColumns?.includes(option.key) ?? false
                  return (
                    <div className='col-md-4' key={option.key}>
                      <label className='form-check form-check-custom form-check-solid align-items-start gap-2 border rounded px-3 py-2 w-100'>
                        <input
                          className='form-check-input mt-1'
                          type='checkbox'
                          checked={checked}
                          onChange={() => handleToggleSelectedColumn(option.key)}
                          disabled={exporting}
                        />
                        <span className='form-check-label fw-medium text-gray-800'>{option.label}</span>
                      </label>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className='alert alert-light border mt-4 mb-0 text-dark bg-white'>
              {loadingClaims ? (
                <span className='text-dark'>Loading claims...</span>
              ) : (
                <span className='text-dark'>
                  <strong>{filteredClaims.length}</strong> claim(s) match the current filters.
                </span>
              )}
            </div>
            {error ? <div className='alert alert-danger mt-3 mb-0'>{error}</div> : null}
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-light' onClick={handleClear} disabled={exporting}>
              Clear Filters
            </button>
            <button type='button' className='btn btn-light' onClick={onClose} disabled={exporting}>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-primary'
              onClick={handleExport}
              disabled={
                exporting ||
                loadingClaims ||
                !form.selectedColumns ||
                form.selectedColumns.length === 0 ||
                filteredClaims.length === 0
              }
            >
              {exporting ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CrewClaimExportModal
